/**
 * Experiment exposure tracking for Mixpanel.
 *
 * Fires Mixpanel's native `$experiment_started` exposure event so the Mixpanel
 * Experiments report can attribute downstream behaviour to the right variant.
 * Event/property names match Mixpanel's defaults ("Experiment name" / "Variant
 * name") so the report works with no project-side configuration.
 *
 * Two entry points:
 *   1. window.trackExperimentExposure({ experiment, variant, source, props })
 *      Generic emitter — used by the Algolia hook below and by any custom /
 *      Option-B (pre-paint client-side bucketing) theme test.
 *   2. Algolia A/B test auto-exposure — reads abTestID / abTestVariantID off the
 *      InstantSearch results (search AND collection browse, same products index)
 *      and forwards exposure.
 *
 * IMPORTANT: theme/component tests served by the Mixpanel Feature Flags SDK
 * (Option A) are auto-exposed by the SDK when `mixpanel.flags.get_variant_value()`
 * is evaluated — those tests must NOT also call window.trackExperimentExposure,
 * or exposure would be double-counted. This module is for Algolia + custom tests.
 *
 * Consent: Mixpanel only loads behind OneTrust C0002, so exposure events only
 * fire for consented users (variant assignment itself is never gated). If Mixpanel
 * is not present at exposure time we no-op, matching the other *-tracking.js files.
 */
(function () {
  'use strict';

  // Dedupe. Mixpanel wants exposure on *actual* exposure, once — not on every
  // event and not merely on assignment. Fire at most once per session per
  // experiment+variant. Once-per-session (not once-ever) keeps the 90-day
  // attribution window alive for returning visitors without spamming.
  var sentThisSession = new Set();
  var SESSION_PREFIX = 'mp_exp_exposed::';

  function alreadySent(key) {
    if (sentThisSession.has(key)) return true;
    try {
      if (sessionStorage.getItem(SESSION_PREFIX + key) === '1') {
        sentThisSession.add(key);
        return true;
      }
    } catch (e) {
      // sessionStorage unavailable (private mode) — in-memory dedupe still applies
    }
    return false;
  }

  function markSent(key) {
    sentThisSession.add(key);
    try {
      sessionStorage.setItem(SESSION_PREFIX + key, '1');
    } catch (e) {
      // ignore
    }
  }

  function resolvePageType() {
    var t = (window.Shopify && window.Shopify.template_name) || '';
    var map = { index: 'homepage', product: 'pdp', collection: 'category', search: 'search', cart: 'cart' };
    if (t) {
      // template.name can be e.g. "product.alternate" — key off the base.
      var base = String(t).split('.')[0];
      return map[base] || base || 'other';
    }
    return 'other';
  }

  /**
   * Generic exposure emitter.
   * @param {{experiment: string, variant: (string|number), source?: string, props?: object}} opts
   * @returns {boolean} true if an event was sent
   */
  function trackExperimentExposure(opts) {
    try {
      if (!opts || !opts.experiment) return false;
      if (opts.variant === undefined || opts.variant === null || opts.variant === '') return false;

      // Mixpanel present? (consent granted + SDK loaded). No-op otherwise.
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return false;
      }

      // Skip bots, like every other emitter.
      if (typeof isBot === 'function' && isBot()) {
        return false;
      }

      var experiment = String(opts.experiment);
      var variant = String(opts.variant);
      var key = experiment + '::' + variant;

      if (alreadySent(key)) return false;

      var payload = {
        // Mixpanel Experiments report defaults — keep these names exact.
        'Experiment name': experiment,
        'Variant name': variant,
        // Context. Super-properties (Display Language / Market / Country /
        // Is Logged In / Customer Type) attach automatically.
        Source: opts.source || 'unknown',
        'Page Type': resolvePageType(),
        'Current URL': window.location.href,
        'User Agent': navigator.userAgent,
      };

      if (opts.props && typeof opts.props === 'object') {
        Object.keys(opts.props).forEach(function (k) {
          if (!(k in payload)) payload[k] = opts.props[k];
        });
      }

      mixpanel.track('$experiment_started', payload);
      markSent(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Expose for the Algolia hook + custom / Option-B theme tests.
  window.trackExperimentExposure = trackExperimentExposure;

  // ---------------------------------------------------------------------------
  // Exposure-on-view (Option-B / custom theme tests)
  // ---------------------------------------------------------------------------
  // Fires exposure when the variant surface is actually SEEN (not on assignment),
  // matching Mixpanel's "send exposure, not assignment" rule. Assignment happens
  // pre-paint/pre-consent (see snippets/experiment-bucketing.liquid); this waits
  // for Mixpanel (i.e. for consent) up to a bound, so a user who consents shortly
  // after seeing the surface is still recorded. Flag-driven Option-A tests must
  // NOT use this — the Feature Flags SDK auto-exposes on evaluation.

  function whenMixpanelReady(cb, maxWaitMs) {
    var waited = 0;
    var step = 1000;
    (function check() {
      if (typeof window.mixpanel !== 'undefined' && window.mixpanel.track) {
        cb();
        return;
      }
      waited += step;
      if (waited >= (maxWaitMs || 20000)) return; // give up — user never consented in window
      setTimeout(check, step);
    })();
  }

  // Fire `cb` once, when `el` first intersects the viewport. threshold 0 = any pixel
  // (0.5 can never be met by elements taller than ~2x the viewport); rootMargin lets
  // the caller act just before the surface is seen. No IntersectionObserver / no el →
  // fire immediately. Shared by the exposure and flag engines.
  function onceVisible(el, opts, cb) {
    opts = opts || {};
    if (el && typeof window.IntersectionObserver === 'function') {
      var io = new window.IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              io.disconnect();
              cb();
              break;
            }
          }
        },
        { threshold: opts.threshold != null ? opts.threshold : 0, rootMargin: opts.rootMargin || '0px' },
      );
      io.observe(el);
    } else {
      cb();
    }
  }

  function experimentExposeOnView(el, opts) {
    if (!el || !opts) return;
    var fired = false;
    onceVisible(el, { threshold: 0 }, function () {
      if (fired) return;
      fired = true;
      whenMixpanelReady(function () {
        trackExperimentExposure(opts);
      });
    });
  }

  window.experimentExposeOnView = experimentExposeOnView;

  // ---------------------------------------------------------------------------
  // Flag-driven variant (Option A) — for below-the-fold tests
  // ---------------------------------------------------------------------------
  // Resolves a Mixpanel feature flag when `el` nears the viewport (rootMargin lead,
  // so the variant is applied before the section is actually seen — no on-arrival
  // shift), then calls onVariant(variant). Evaluating the flag AUTO-FIRES the
  // $experiment_started exposure event, so this is for flag-driven tests only — do
  // NOT also call trackExperimentExposure for the same test. Above-the-fold tests
  // should use pre-paint (window.Experiments) instead, since the flag is async.
  function experimentFlagVariant(el, flag, onVariant, opts) {
    if (!flag || typeof onVariant !== 'function') return;
    opts = opts || {};
    var fallback = opts.fallback != null ? opts.fallback : 'control';
    var resolved = false;

    function resolve() {
      if (resolved) return;
      resolved = true;
      var waited = 0;
      (function check() {
        var mp = window.mixpanel;
        if (mp && mp.flags && typeof mp.flags.get_variant_value === 'function') {
          mp.flags
            .get_variant_value(flag, fallback)
            .then(function (v) {
              try {
                onVariant(v);
              } catch (e) {
                /* caller error */
              }
            })
            .catch(function () {
              try {
                onVariant(fallback);
              } catch (e) {
                /* caller error */
              }
            });
          return;
        }
        waited += 200;
        if (waited >= (opts.maxWaitMs || 20000)) {
          try {
            onVariant(fallback); // Mixpanel never loaded → apply fallback (no exposure fired), symmetric with .catch
          } catch (e) {
            /* caller error */
          }
          return;
        }
        setTimeout(check, 200);
      })();
    }

    onceVisible(el, { rootMargin: opts.rootMargin || '400px 0px' }, resolve);
  }

  window.experimentFlagVariant = experimentFlagVariant;

  // ---------------------------------------------------------------------------
  // Declarative auto-init — wire experiments from data- attributes so sections
  // don't hand-roll inline init scripts.
  // ---------------------------------------------------------------------------
  //   Pre-paint exposure (Option B): on the surface element —
  //     data-exp-expose="<Experiment name>" data-exp-key="<assignment slug>" [data-exp-source]
  //     Variant is read from window.Experiments.get(key); $experiment_started fires on view.
  //   Flag-driven (Option A): on the container element —
  //     data-exp-flag="<mixpanel flag key>" [data-exp-root-margin]
  //     Descendants tagged data-exp-variant="control|treatment|all" are shown/hidden for the
  //     resolved variant (control = default rendered; treatment hidden until resolved). The
  //     flag eval auto-fires exposure.
  function applyVariantVisibility(root, variant) {
    var v = typeof variant === 'string' ? variant.trim().toLowerCase() : variant;
    if (v !== 'treatment') return; // control = the default already rendered
    var hide = root.querySelectorAll('[data-exp-variant="control"]');
    for (var i = 0; i < hide.length; i++) hide[i].classList.add('exp-hidden');
    var show = root.querySelectorAll('[data-exp-variant="treatment"]');
    for (var j = 0; j < show.length; j++) show[j].classList.remove('exp-hidden');
    // If this container is (or sits inside) a Dawn slider-component, re-init it — it was
    // set up against the pre-swap slide set and recomputes via a ResizeObserver on its own
    // box, which a class toggle on flex children doesn't trigger. Scoped to the actual
    // slider so we don't fire a global resize for plain (non-carousel) grids.
    try {
      var slider = (root.closest && root.closest('slider-component')) || (root.querySelector && root.querySelector('slider-component'));
      if (slider && typeof slider.reinit === 'function') slider.reinit();
    } catch (e) {
      /* no-op */
    }
  }

  function autoInitExperiments() {
    if (!document.querySelectorAll) return;

    // `:not([data-exp-wired])` + marking each element makes re-runs idempotent
    // (shopify:section:load re-renders, dynamic injection) — no double wiring/exposure.

    // Option B — pre-paint exposure surfaces.
    var exposeEls = document.querySelectorAll('[data-exp-expose]:not([data-exp-wired])');
    for (var i = 0; i < exposeEls.length; i++) {
      (function (el) {
        if (!window.Experiments) return;
        var variant = window.Experiments.get(el.getAttribute('data-exp-key'));
        if (!variant) return;
        el.setAttribute('data-exp-wired', '');
        experimentExposeOnView(el, {
          experiment: el.getAttribute('data-exp-expose'),
          variant: variant,
          source: el.getAttribute('data-exp-source') || 'shopify',
        });
      })(exposeEls[i]);
    }

    // Option A — flag-driven variant visibility.
    var flagEls = document.querySelectorAll('[data-exp-flag]:not([data-exp-wired])');
    for (var k = 0; k < flagEls.length; k++) {
      (function (el) {
        el.setAttribute('data-exp-wired', '');
        experimentFlagVariant(
          el,
          el.getAttribute('data-exp-flag'),
          function (variant) {
            applyVariantVisibility(el, variant);
          },
          { rootMargin: el.getAttribute('data-exp-root-margin') || '400px 0px' },
        );
      })(flagEls[k]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInitExperiments);
  } else {
    autoInitExperiments();
  }
  // Re-arm for Shopify theme-editor section re-renders + dynamically injected sections.
  document.addEventListener('shopify:section:load', autoInitExperiments);

  // ---------------------------------------------------------------------------
  // Algolia A/B test auto-exposure
  // ---------------------------------------------------------------------------
  // Algolia assigns variants server-side (sticky per userToken) and returns
  // abTestID / abTestVariantID on the search response. The SAME InstantSearch
  // products index powers both /search and collection browse, so a ranking test
  // exposes browsing users too — we hook the helper `result` event (fires on
  // both surfaces) rather than gating on the /search path.

  var algoliaAttached = false; // 'result' listener attached
  var algoliaPolling = false;

  // Optional human labels for Algolia tests, keyed by numeric abTestID. Set from
  // a theme snippet/settings if desired, e.g.
  //   window.ALGOLIA_EXPERIMENT_LABELS = { '42': 'Search Ranking — Brand Boost' };
  // Falls back to a stable generated name (no code change when tests rotate).
  function algoliaExperimentName(abTestID) {
    var labels = window.ALGOLIA_EXPERIMENT_LABELS || {};
    return labels[String(abTestID)] || 'Algolia AB Test ' + abTestID;
  }

  // abTestVariantID is 1-based; map to letters for readability (1 -> A, 2 -> B …).
  function variantLabel(variantId) {
    var n = parseInt(variantId, 10);
    if (n >= 1 && n <= 26) return String.fromCharCode(64 + n);
    return 'variant-' + variantId;
  }

  function readAlgoliaABTest() {
    try {
      var search =
        window.algoliaShopify && window.algoliaShopify.instantsearch && window.algoliaShopify.instantsearch.search;
      if (!search || !search.helper) return null;

      var results = search.helper.lastResults;
      if (!results) return null;

      var abTestID = results.abTestID;
      var abTestVariantID = results.abTestVariantID;

      // Fallback to the raw response if the helper doesn't surface them directly.
      if ((abTestID === undefined || abTestVariantID === undefined) && results._rawResults && results._rawResults[0]) {
        var raw = results._rawResults[0];
        if (abTestID === undefined) abTestID = raw.abTestID;
        if (abTestVariantID === undefined) abTestVariantID = raw.abTestVariantID;
      }

      if (abTestID === undefined || abTestVariantID === undefined || abTestVariantID === null) {
        return null; // no A/B test running on this query
      }

      return { abTestID: abTestID, abTestVariantID: abTestVariantID };
    } catch (e) {
      return null;
    }
  }

  var algoliaWaitPending = false;

  function sendAlgoliaExposure(ab) {
    trackExperimentExposure({
      experiment: algoliaExperimentName(ab.abTestID),
      variant: variantLabel(ab.abTestVariantID),
      source: 'algolia',
      props: {
        'Algolia AB Test ID': ab.abTestID,
        'Algolia Variant ID': ab.abTestVariantID,
      },
    });
  }

  function fireAlgoliaExposure() {
    var ab = readAlgoliaABTest();
    if (!ab) return;
    // Mixpanel is consent-gated and may not be loaded at the first/only `result`
    // event. Wait for it (bounded) so a user who consents shortly after the search
    // is still recorded — rather than silently relying on another result event to
    // re-fire. Dedupe in trackExperimentExposure keeps it to one event.
    if (typeof window.mixpanel !== 'undefined' && window.mixpanel.track) {
      sendAlgoliaExposure(ab);
      return;
    }
    if (algoliaWaitPending) return;
    algoliaWaitPending = true;
    whenMixpanelReady(function () {
      algoliaWaitPending = false;
      var latest = readAlgoliaABTest();
      if (latest) sendAlgoliaExposure(latest);
    });
  }

  function initAlgoliaExposure() {
    if (algoliaAttached || algoliaPolling) return;
    algoliaPolling = true;

    var attempts = 0;
    var poll = setInterval(function () {
      attempts++;
      var search =
        window.algoliaShopify && window.algoliaShopify.instantsearch && window.algoliaShopify.instantsearch.search;

      if (search && search.helper) {
        clearInterval(poll);
        algoliaPolling = false;
        if (algoliaAttached) return;
        algoliaAttached = true;

        // Catch a test already resolved before we attached (initial render).
        fireAlgoliaExposure();

        // Fire on each subsequent results render; dedupe keeps it once/session.
        search.helper.on('result', fireAlgoliaExposure);
        return;
      }

      if (attempts >= 100) {
        // ~10s; give up — re-armed by algolia.finishedInitialization below.
        clearInterval(poll);
        algoliaPolling = false;
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlgoliaExposure);
  } else {
    initAlgoliaExposure();
  }

  // Re-arm if Algolia signals (re)initialization, matching the other emitters.
  window.addEventListener('algolia.finishedInitialization', function () {
    if (!algoliaAttached) initAlgoliaExposure();
  });
})();
